import { Calendar } from "@/models/calendar";
import { CalendarService } from "@/services/calendar-service";
import { GoogleCalendarService } from "@/services/google-calendar-service";
import * as FullCalendarApi from "@fullcalendar/core";
import { FolderUser } from "@/models/tasks/folder-user";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import { TaskService } from "@/services/task-service";
import { EventInput } from "@fullcalendar/core";
import { printLocalDateWeekday} from "./pretty-print";
import { ContactAddress, Sale } from "@/models/base";
import Crm from "@/models/crm/crm";
import { i18n } from "@/setup/i18n-setup";
import { UserConfigService } from "@/services";
import { SaleSettings } from "@/models/user-configs/sale-settings";
import { CrmSettings } from "@/models/user-configs/crm-settings";

function stripHtmlToText(html:string):string {
	let el = document.createElement("div");
	el.innerHTML = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li)>/gi, "\n");
	return (el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}


export enum CalendarViewType {
	Day = 1,
	Week = 2,
	Month = 3,
	DayPerCalendar = 4
}

export interface IOnDropProps {
	event:EventInput,
	revert:Function
}

const KEY_LOCAL_STORAGE_DEFAULT_CALENDAR_VIEW = "calendar-default-view";

export class CalendarState {
	userSaleSettings:SaleSettings = UserConfigService.getSaleSettings();
	crmSettings:CrmSettings = UserConfigService.getCrmSettings();

	googleCalendars:gapi.client.calendar.CalendarListEntry[] = [];
	selectedGoogleCalendars:gapi.client.calendar.CalendarListEntry[] = [];
	calendars:Calendar[] = [];
	selectedCalendars:Calendar[] = [];
	taskFolders:FolderUser[] = [];
	selectedTaskFolders:FolderUser[] = [];

	private calendarApis:FullCalendarApi.Calendar[] = [];

	currentViewType:CalendarViewType = CalendarViewType.Week;
	prevVisibleDate:Date = new Date();
	currentVisibleDate:Date = new Date();
	nextVisibleDate:Date = new Date();
	get defaultCalendarView():string{
		let num = parseInt(localStorage.getItem(KEY_LOCAL_STORAGE_DEFAULT_CALENDAR_VIEW) || "");
		if (Number.isNaN(num) || num == 0){
			num = CalendarViewType.Day;
		}
		return this.viewTypeToCalendarViewType(num);
	}

	private height:number = 0;


	constructor() {
		let num = parseInt(localStorage.getItem(KEY_LOCAL_STORAGE_DEFAULT_CALENDAR_VIEW) || "");
		if (Number.isNaN(num) || num == 0){
			num = CalendarViewType.Day;
		}
		this.currentViewType = num;
		this.setVisibleDate(this.currentVisibleDate);
	}

	async fetchCals() {
		let googleCals = [] as gapi.client.calendar.CalendarListEntry[];
		if(GoogleCalendarService.isLoggedIn()) {
			try {
				googleCals = await GoogleCalendarService.getCalendars();
			} catch(err) {
				console.error(err);
			}
		}
		let taskFolders = [] as FolderUser[];
		if (AuthService.hasModule(MODULE.TAKEN)){
			taskFolders = await TaskService.getFolders();
		}

		try {
			this.calendars = await CalendarService.getCalendars();
		} catch(err) {
		}
		this.googleCalendars = googleCals;
		this.taskFolders = taskFolders;
	}

	setVisibleDate(date:Date) {
		let currentDate = date;
		let prevVisibleDate = new Date(date.getTime());
		let nextVisibleDate = new Date(date.getTime());
		if (this.currentViewType == CalendarViewType.Day || this.currentViewType == CalendarViewType.DayPerCalendar) {
			prevVisibleDate.setDate(date.getDate() - 1);
			nextVisibleDate.setDate(date.getDate() + 1);
		} else if (this.currentViewType == CalendarViewType.Week) {
			let currentDay = date.getDay() - 1;
			if (currentDay < 0) {
				currentDay = 6;
			}
			currentDate.setDate(currentDate.getDate() - currentDay);
			prevVisibleDate = new Date(currentDate.getTime());
			nextVisibleDate = new Date(currentDate.getTime());
			prevVisibleDate.setDate(currentDate.getDate() - 7);
			nextVisibleDate.setDate(currentDate.getDate() + 7);
		} else if (this.currentViewType == CalendarViewType.Month) {
			currentDate.setDate(1);
			prevVisibleDate.setDate(0);
			prevVisibleDate.setDate(1);
			nextVisibleDate.setDate(32);
			nextVisibleDate.setDate(1);
		}
		this.currentVisibleDate = currentDate;
		this.nextVisibleDate = nextVisibleDate;
		this.prevVisibleDate = prevVisibleDate;
		for (let api of this.calendarApis){
			api.changeView(this.defaultCalendarView, this.currentVisibleDate);
		}
	}

	setViewTypeDay() {
		this.setViewType(CalendarViewType.Day);
	}

	setViewTypeWeek() {
		this.setViewType(CalendarViewType.Week);
	}

	setViewTypeMonth() {
		this.setViewType(CalendarViewType.Month);
	}

	setViewTypePerCalendar(){
		this.setViewType(CalendarViewType.DayPerCalendar);
	}

	refreshView() {
		if (this.currentViewType == CalendarViewType.Day) {
			this.setViewTypeDay();
		} else if (this.currentViewType == CalendarViewType.Week) {
			this.setViewTypeWeek();
		} else if (this.currentViewType == CalendarViewType.Month) {
			this.setViewTypeMonth();
		}else {
			this.setViewTypePerCalendar();
		}
	}

	calendarNext() {
		this.setVisibleDate(this.nextVisibleDate);
		this.refreshView();
	}

	calendarPrev() {
		this.setVisibleDate(this.prevVisibleDate);
		this.refreshView();
	}

	calendarGoToToday() {
		this.setVisibleDate(new Date());
		this.refreshView();
	}

	getWeekNumber():number {
		let date = this.getWeekThursday();
		let week1 = new Date(date.getFullYear(), 0, 4);
		return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
	}

	getWeekYear():number {
		return this.getWeekThursday().getFullYear();
	}

	private getWeekThursday():Date {
		let date = new Date(this.currentVisibleDate.getTime());
		date.setHours(0, 0, 0, 0);
		date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
		return date;
	}

	getTitleKey():string {
		if (this.currentViewType == CalendarViewType.DayPerCalendar) {
			return printLocalDateWeekday(this.currentVisibleDate,"short");
		}
		return `common.months.${this.currentVisibleDate.getMonth() + 1}`;
	}

	async saveEvent(e: IOnDropProps) {
		if (!e.event.extendedProps) {
			return;
		}
		let props = e.event.extendedProps as any;
		try{
			if (props.isGoogle) {
				await this.saveGoogleEvent(e);
			} else if(props.isTask){
				await this.saveTask(e);
			} else {
				await this.saveLocalEvent(e);
			}
		}catch(e:any){
			console.error(e);
			if (e.revert){
				e.revert();
			}
		}
	}

	async saveTask(e:IOnDropProps){
		let props = e.event.extendedProps as any;
		let task = await TaskService.getTask(props.id as number);
		let folder = await TaskService.getFolder(task.FolderID);
		if (!folder.IsOwner){
			e.revert();
			return;
		}
		task.DueDate = e.event.start as Date;
		await TaskService.putTask(task);
	}

	async saveLocalEvent(e:IOnDropProps){
		let props = e.event.extendedProps as any;
		let originalEvent = await CalendarService.getEvent(props.id as number);
		originalEvent.AllDay = e.event.allDay || false;
		originalEvent.StartTime = e.event.start as Date;
		if (e.event.allDay) {
			originalEvent.EndTime = e.event.start as Date;
		} else {
			let endDate = e.event.end as Date;
			if (!endDate) {
				endDate = new Date(originalEvent.StartTime.getTime());
				endDate.setHours(endDate.getHours() + 1);
			}
			originalEvent.EndTime = endDate;
		}
		await CalendarService.putEvents([originalEvent]);
	}

	async saveGoogleEvent(e:IOnDropProps){
		let props = e.event.extendedProps as any;
		let originalEvent = await GoogleCalendarService.getEvent(props.id as string, props.calendarId as string);
		if (e.event.allDay) {
			originalEvent.start.date = (e.event.start as Date).toISOString().substr(0, 10);
			originalEvent.end.date = (e.event.start as Date).toISOString().substr(0, 10);
		} else {
			originalEvent.start.dateTime = (e.event.start as Date).toISOString();
			originalEvent.end.dateTime = (e.event.end as Date).toISOString();
		}
		await GoogleCalendarService.putEvent(originalEvent, props.calendarId);
	}

	setCalendarApis(apis:FullCalendarApi.Calendar[]) {
		this.calendarApis = apis;
		for (let api of this.calendarApis) {
			api.setOption("height", this.height);
		}
		this.updateViewType();
	}

	refetchEvents(){
		for (let api of this.calendarApis) {
			api.refetchEvents();
		}
	}

	setHeight(height:number){
		this.height = height-30;
		for (let api of this.calendarApis) {
			api.setOption("height", this.height);
		}
	}

	private viewTypeToCalendarViewType(viewType:CalendarViewType):string {
		if (viewType == CalendarViewType.Week){
			return "timeGridWeek";
		}else if (viewType == CalendarViewType.Month){
			return "dayGridMonth";
		}
		return "timeGridDay";
	}

	private setViewType(viewType:CalendarViewType){
		this.currentViewType = viewType;
		this.setVisibleDate(this.currentVisibleDate);
		localStorage.setItem(KEY_LOCAL_STORAGE_DEFAULT_CALENDAR_VIEW, viewType.toString());
		this.updateViewType();
	}

	updateViewType(){
		for (let api of this.calendarApis){
			api.changeView(this.defaultCalendarView, this.currentVisibleDate);
		}
	}

	// eslint-disable-next-line max-lines-per-function
	private onEventRenderAddTooltip(el:HTMLElement, sale:Sale){
		let div = document.createElement("div");
		function createLine(text:string, classes:string = ""){
			if (!text) return;
			let el = document.createElement("div");
			el.innerText = text;
			el.className = classes;
			div.appendChild(el);
		}

		div.className = "event-tooltip";
		let link = document.createElement("a");
		link.innerText = `${sale.getTypeName(i18n.locale)} ${sale.ComputedFriendlyID}`;
		link.className = "document-number";
		link.addEventListener("click", (e:Event)=>{
			e.preventDefault();
			e.stopPropagation();
			window.open(`/sales/info/${sale.ID}, "_blank"`);
		});
		div.appendChild(link);

		function createAddressLines(address:ContactAddress){
			createLine(address.CompanyOrFullName);
			createLine(address.AddressLine1);
			createLine(address.AddressLine2);
			createLine(address.PostalAndCity);
			createLine(address.Phone);
			createLine(address.Email, "email");
		}

		let typeConf = this.userSaleSettings.getSaleConfig(sale.TypeID);
		if (!typeConf) return;
		if (typeConf.CalendarToShowHoverFields.length == 0) {
			if (sale.DeliveryAddress && sale.DeliveryAddress.ID != 0){
				createAddressLines(sale.DeliveryAddress);
			}else if (sale.InvoiceAddress){
				createAddressLines(sale.InvoiceAddress);
			}
		}else{
			for (let prop of typeConf.CalendarToShowHoverFields) {
				let v:any = sale;
				let parts = prop.Prop.split(".");
				for (let p of parts) {
					v = v[p];
					if (!v) break;
				}
				createLine(v);
			}
		}

		el.addEventListener("mouseenter", ()=>{
			let rect = el.getBoundingClientRect();
			div.style.left = `${rect.left}px`;
			div.style.bottom = `${window.innerHeight-rect.top}px`;
			div.style.width = `${rect.width}px`;
		});
		el.appendChild(div);
	}

	private onEventRenderOverrideColor(el:HTMLElement, event:FullCalendarApi.EventApi){
		if (event._def.extendedProps.colorOverride){
			el.style.backgroundColor = event._def.extendedProps.colorOverride;
		}
	}

	private onEventRenderAddSaleFields(el:HTMLElement, sale:Sale){
		let typeConf = this.userSaleSettings.getSaleConfig(sale.TypeID);
		if (!typeConf) return;
		if (typeConf.CalendarToShowFields.length == 0) return;
		let div = document.createElement("div");
		div.style.borderTop = "solid 1px white";
		for (let prop of typeConf.CalendarToShowFields) {
			let v:any = sale;
			let parts = prop.Prop.split(".");
			for (let p of parts) {
				v = v[p];
				if (!v) break;
			}
			let d = document.createElement("div");
			d.innerText = v || "";
			div.append(d);
		}
		el.append(div);
	}

	private onEventRenderAddCrmFields(el:HTMLElement, crm:Crm){
		if (this.crmSettings.CalendarToShowFields.length == 0) return;
		let div = document.createElement("div");
		div.style.borderTop = "solid 1px white";
		for (let prop of this.crmSettings.CalendarToShowFields) {
			let v:any = crm;
			let parts = prop.Prop.split(".");
			for (let p of parts) {
				v = v[p];
				if (!v) break;
			}
			let d = document.createElement("div");
			d.innerText = v || "";
			div.append(d);
		}
		el.append(div);
	}

	// eslint-disable-next-line max-lines-per-function
	private onEventRenderAddCrmTooltip(el:HTMLElement, crm:Crm){
		let div = document.createElement("div");
		function createLine(text:string, classes:string = ""){
			if (!text) return;
			let el = document.createElement("div");
			el.innerText = text;
			el.className = classes;
			div.appendChild(el);
		}

		div.className = "event-tooltip";
		let link = document.createElement("a");
		link.innerText = `${i18n.t("components.crm.crm")} ${crm.ID}`;
		link.className = "document-number";
		link.addEventListener("click", (e:Event)=>{
			e.preventDefault();
			e.stopPropagation();
			window.open(`/crm/edit/${crm.ID}`, "_blank");
		});
		div.appendChild(link);

		if (this.crmSettings.CalendarToShowHoverFields.length == 0) {
			let address = crm.Contact ? crm.Contact.MainAddress : null;
			if (address && address.ID != 0){
				createLine(address.CompanyOrFullName);
				createLine(address.AddressLine1);
				createLine(address.AddressLine2);
				createLine(address.PostalAndCity);
				createLine(address.Phone);
				createLine(address.Email, "email");
			}
		}else{
			for (let prop of this.crmSettings.CalendarToShowHoverFields) {
				let v:any = crm;
				let parts = prop.Prop.split(".");
				for (let p of parts) {
					v = v[p];
					if (!v) break;
				}
				createLine(v || "");
			}
		}

		el.addEventListener("mouseenter", ()=>{
			let rect = el.getBoundingClientRect();
			div.style.left = `${rect.left}px`;
			div.style.bottom = `${window.innerHeight-rect.top}px`;
			div.style.width = `${rect.width}px`;
		});
		el.appendChild(div);
	}

	private onEventRenderCrm(el:HTMLElement, crm:Crm){
		if (this.crmSettings.CalendarToShowFields.length != 0) {
			const titleElement = el.querySelector(".fc-title");
			if (titleElement) {
				titleElement.innerHTML = "";
			}
		}
		this.onEventRenderAddCrmFields(el, crm);
		this.onEventRenderAddCrmTooltip(el, crm);
	}

	private onEventRenderAddLocation(el:HTMLElement, location:string){
		let div = document.createElement("div");
		div.style.borderTop = "solid 1px white";
		div.innerText = location;
		el.append(div);
	}

	onEventRender(event:{event:FullCalendarApi.EventApi, el:HTMLElement, isMirror:boolean}){
		this.onEventRenderOverrideColor(event.el, event.event);
		if (event.isMirror) return;
		let location = event.event._def.extendedProps.location as string | undefined;
		if (location) {
			this.onEventRenderAddLocation(event.el, location);
		}
		let description = event.event._def.extendedProps.description as string | undefined;
		let tooltip = [location, description ? stripHtmlToText(description) : ""].filter(t => t).join("\n");
		if (tooltip) {
			event.el.title = tooltip;
		}
		let linkedSale = event.event._def.extendedProps.sale as Sale | null;
		let crm = event.event._def.extendedProps.crm as Crm | null;
		if (crm && crm.ID != 0 && (!linkedSale || linkedSale.ID == 0)) {
			this.onEventRenderCrm(event.el, crm);
			return;
		}
		if (!event.event._def.extendedProps.sale) return;
		let sale = event.event._def.extendedProps.sale as Sale;
		if (sale.ID == 0) return;
		let typeConf = this.userSaleSettings.getSaleConfig(sale.TypeID);
		if (!typeConf) return;
		if (typeConf.CalendarToShowFields.length != 0) {
			const titleElement = event.el.querySelector(".fc-title");
			if (titleElement) {
				titleElement.innerHTML = "";
			}
		};
		this.onEventRenderAddSaleFields(event.el, sale);
		this.onEventRenderAddTooltip(event.el, sale);
	}
}